// ui/Menu — a button that opens a small list of actions (Design Standard v1).
// Styles are the kit's .ds-menu / .ds-menu__list / .ds-menu__item in ds.css.
// Closes on click-away, Escape, or when an item runs; ArrowUp/Down move focus.
//   <Menu label="New" trigger={<Button …/>} align="right">
//     <MenuItem onSelect={…}>Add customer</MenuItem>
//     <MenuSeparator />
//     <MenuItem danger onSelect={…}>Log out</MenuItem>
//   </Menu>
import React, { cloneElement, createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import './ds.css';

const Ctx = createContext({ close: () => {} });

export default function Menu({ trigger, children, align = 'right', className = '', onOpenChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();

  const close = useCallback(() => setOpen(false), []);
  useEffect(() => { onOpenChange?.(open); }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const items = [...(listRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])') || [])];
      if (!items.length) return;
      e.preventDefault();
      const i = items.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
      items[next].focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    // Focus the first item so the keyboard works at once.
    const first = listRef.current?.querySelector('[role="menuitem"]:not([disabled])');
    first?.focus({ preventScroll: true });
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const button = cloneElement(trigger, {
    onClick: (e) => { trigger.props.onClick?.(e); setOpen((v) => !v); },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? id : undefined,
  });

  return (
    <div ref={rootRef} className={`ds-menu ${className}`}>
      {button}
      {open ? (
        <Ctx.Provider value={{ close }}>
          <div ref={listRef} id={id} role="menu" className={`ds-menu__list ds-menu__list--${align}`}>
            {children}
          </div>
        </Ctx.Provider>
      ) : null}
    </div>
  );
}

export function MenuItem({ children, onSelect, icon = null, danger = false, disabled = false, keepOpen = false, className = '', ...rest }) {
  const { close } = useContext(Ctx);
  return (
    <button
      type="button"
      role="menuitem"
      className={`ds-menu__item ${danger ? 'is-danger' : ''} ${className}`}
      disabled={disabled}
      onClick={(e) => { onSelect?.(e); if (!keepOpen) close(); }}
      {...rest}
    >
      {icon ? <span className="ds-menu__icon" aria-hidden="true">{icon}</span> : null}
      <span className="ds-menu__text">{children}</span>
    </button>
  );
}

export function MenuLabel({ children }) {
  return <div className="ds-menu__label">{children}</div>;
}

export function MenuSeparator() {
  return <div className="ds-menu__sep" role="separator" />;
}
